import type { TFunction } from "i18next";

import createDockPageRegistry, { type DockDetailRoute, type DockPageMeta } from "~/components/dock/dockPageRegistry";

const createProducePages = (
	t: TFunction<"common">,
	isPlanBuilderEnabled: boolean,
	productionCenterEnabled: boolean
): DockPageMeta[] => [
	{
		hideRibbon: true,
		id: "dashboard",
		path: "/produce/dashboard",
		title: t("dock.pages.produce.dashboard")
	},
	{
		hideRibbon: true,
		id: "production-calendar",
		path: "/produce/production-calendar",
		title: t("dock.pages.produce.productionCalendar")
	},
	{
		id: "production-schedule",
		path: "/produce/production/schedule",
		routePattern: "production/schedule/*",
		title: t("dock.pages.produce.productionSchedule"),
		...(productionCenterEnabled && {
			keepOwnLink: true,
			title: t("produce:productionCenter.title")
		})
	},
	{
		id: "event-schedule",
		path: "/produce/production/schedule/events",
		title: t("dock.pages.produce.eventSchedule")
	},
	...(isPlanBuilderEnabled
		? [
				{
					id: "plan-builder",
					path: "/produce/production/plan-builder",
					title: t("dock.pages.produce.planBuilder")
				}
			]
		: []),
	{
		id: "space-planning",
		path: "/produce/space-planning",
		title: t("dock.pages.produce.spacePlanning")
	},
	{
		id: "labor-schedule",
		path: "/produce/labor/schedule",
		routePattern: "labor/schedule/*",
		title: t("dock.pages.produce.laborSchedule")
	},
	{
		id: "labor-crew",
		path: "/produce/labor/crew",
		title: t("dock.pages.produce.laborCrew")
	},
	{
		id: "labor-rates",
		path: "/produce/labor/rates",
		routePattern: "labor/rates/:siteLocationId?",
		title: t("dock.pages.produce.laborRates")
	},
	{
		id: "labor-tasks",
		path: "/produce/labor/tasks",
		routePattern: "labor/tasks/*",
		title: t("dock.pages.produce.laborTasks")
	},
	{
		id: "purchase-orders",
		path: "/produce/purchasing/orders",
		routePattern: "purchasing/orders/*",
		title: t("dock.pages.produce.purchaseOrders")
	},
	{
		id: "vendor-spend",
		path: "/produce/purchasing/spend",
		title: t("dock.pages.produce.vendorSpend")
	},
	{
		id: "cost-report",
		path: "/produce/production/cost-report",
		title: t("dock.pages.produce.costReport")
	},
	{
		id: "shrink-loss",
		path: "/produce/production/shrink-loss",
		title: t("dock.pages.produce.shrinkLoss")
	},
	{
		id: "vendors",
		path: "/produce/purchasing/vendors",
		title: t("dock.pages.produce.vendors")
	},
	{
		id: "hardgoods",
		path: "/produce/inventory/hardgoods",
		title: t("dock.pages.produce.hardgoods")
	},
	{
		id: "recipes",
		path: "/produce/production/recipes",
		routePattern: "production/recipes/:recipeID?",
		title: t("dock.pages.produce.recipes")
	},
	{
		id: "recipes-link-health",
		path: "/produce/production/recipes/link-health",
		title: t("dock.pages.produce.linkHealth")
	},
	{
		id: "production-items",
		lazyMountUntilActive: true,
		path: "/produce/production/items",
		routePattern: "production/items/*",
		title: t("dock.pages.produce.productionItems")
	},
	{
		id: "material-items",
		path: "/produce/production/material-items",
		routePattern: "production/material-items/*",
		title: t("dock.pages.produce.materialItems")
	},
	{
		id: "supplier-price-imports",
		path: "/produce/production/supplier-price-imports",
		routePattern: "production/supplier-price-imports/:importId?",
		title: t("dock.pages.produce.supplierPriceImports")
	},
	{
		id: "events",
		path: "/produce/production/events",
		routePattern: "production/events/*",
		title: t("dock.pages.produce.events")
	},
	{
		id: "triggers",
		path: "/produce/production/triggers",
		title: t("dock.pages.produce.triggers")
	},
	{
		id: "categories-and-types",
		path: "/produce/capacity/categories-and-types",
		routePattern: "capacity/categories-and-types/:categoryId?",
		title: t("dock.pages.produce.categoriesAndTypes")
	},
	{
		id: "capacity-locations",
		path: "/produce/capacity/locations",
		title: t("dock.pages.produce.capacityLocations")
	},
	{
		id: "overhead-rates",
		path: "/produce/capacity/overhead-rates",
		routePattern: "capacity/overhead-rates/:siteLocationId?",
		title: t("dock.pages.produce.overheadRates")
	},
	{
		id: "settings",
		path: "/produce/settings",
		title: t("dock.pages.produce.settingsGeneral")
	},
	{
		id: "settings-consolidation",
		path: "/produce/settings/consolidation",
		title: t("dock.pages.produce.settingsConsolidation")
	},
	{
		id: "data-transform",
		path: "/produce/data-transform",
		title: t("dock.pages.produce.dataTransform")
	}
];

const createProduceDetailRoutes = (t: TFunction<"common">): DockDetailRoute[] => [
	{
		componentId: "purchase-orders",
		match: (pathname) => {
			const match = pathname.match(/^\/produce\/purchasing\/orders\/(?!vendor\/)([^/]+)/);
			if (!match) return null;
			const [, purchaseOrderId] = match;
			return {
				id: `purchase-order-${purchaseOrderId}`,
				path: `/produce/purchasing/orders/${purchaseOrderId}`,
				title: t("dock.pages.produce.purchaseOrder")
			};
		}
	}
];

const createProduceDockPageRegistry = (
	t: TFunction<"common">,
	isPlanBuilderEnabled: boolean,
	consolidationEnabled: boolean,
	canViewCostReport: boolean,
	productionCenterEnabled: boolean
) =>
	createDockPageRegistry(
		createProducePages(t, isPlanBuilderEnabled, productionCenterEnabled).filter(
			(page) =>
				(consolidationEnabled || page.id !== "settings-consolidation") &&
				(canViewCostReport || page.id !== "cost-report")
		),
		"dashboard",
		createProduceDetailRoutes(t)
	);

export default createProduceDockPageRegistry;
